import { useState } from 'react';
import { useBooking } from '../context/BookingContext';
import NotifyModal from './NotifyModal';
import { useScrollReveal } from '../hooks/useScrollReveal';

const MISO_MINI_IMG = 'https://customer-assets.emergentagent.com/job_heritage-prints-1/artifacts/rabix4e6_5bb4c21e-56a8-45df-88f5-ec642a498813.jpeg';
const GUEST_BOOTH_IMG = 'https://customer-assets.emergentagent.com/job_heritage-prints-1/artifacts/fztzgeo7_f1a70764ecd5223b7e01359713bb6b39.jpg';
const MISO_EXP_IMG = 'https://customer-assets.emergentagent.com/job_heritage-prints-1/artifacts/n7di20da_hf_20260405_002433_ef89a408-ae6b-43f6-acf4-bbcaf0e75bff.png';

const PRODUCTS = [
  {
    id: 'miso-mini',
    live: true,
    image: MISO_MINI_IMG,
    name: 'Miso Mini',
    tagline: "Your event's favourite guest.",
    desc: 'A beautifully designed portable photo booth. Arrives at your venue, prints stunning photos in 20 seconds, and leaves every guest with something to keep.',
    price: '₹18,000',
    cta: 'Book Miso Mini →',
  },
  {
    id: 'guest-booth',
    live: false,
    image: GUEST_BOOTH_IMG,
    name: 'Guest Booth',
    tagline: 'A video guest book for your celebration.',
    desc: 'Guests record heartfelt video messages at your event. You receive the full collection, beautifully edited, after.',
    price: null,
    cta: null,
  },
  {
    id: 'miso-experience',
    live: false,
    image: MISO_EXP_IMG,
    name: 'Miso Experience',
    tagline: 'The full setup.',
    desc: 'The complete MISO installation — photo booth, guest book, and more. For the event that wants to do it all.',
    price: null,
    cta: null,
  },
];

function ProductCard({ product, delay, onBook, onNotify }) {
  const reveal = useScrollReveal(delay);
  const isMini = product.live;

  return (
    <div
      ref={reveal.ref}
      style={reveal.style}
      data-testid={`product-card-${product.id}`}
    >
      <div
        style={{
          borderRadius: '16px',
          border: isMini ? '2px solid rgba(184,92,58,0.3)' : '1px solid rgba(139,74,48,0.12)',
          backgroundColor: isMini ? '#FAF5EF' : '#F5EEE8',
          overflow: 'hidden',
          opacity: isMini ? 1 : 0.82,
          transition: 'opacity 0.3s ease, border-color 0.3s ease',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Image */}
        <div
          className="img-hover-wrap"
          style={{ aspectRatio: '1 / 1', position: 'relative', overflow: 'hidden' }}
        >
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            style={{
              width: '100%', height: '100%', objectFit: 'cover',
              objectPosition: product.id === 'guest-booth' ? 'center top' : 'center',
              filter: isMini ? 'none' : 'saturate(0.7)',
            }}
          />
          <div style={{ position: 'absolute', top: '14px', left: '14px' }}>
            {isMini ? (
              <span className="badge-live" data-testid="product-live-badge">
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#B85C3A', flexShrink: 0 }} />
                Available Now
              </span>
            ) : (
              <span className="badge-soon" data-testid={`product-soon-badge-${product.id}`}>
                Coming Soon
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '24px 24px 28px', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <p style={{ fontFamily: 'Graphik, sans-serif', fontWeight: 500, fontStyle: 'italic', fontSize: '13px', color: '#9E8E7E', marginBottom: '6px' }}>
            {product.tagline}
          </p>
          <h3 className="h3-miso" style={{ marginBottom: '12px', color: isMini ? '#5C2424' : '#7A6B5E' }}>
            {product.name}
          </h3>
          <p className="body-miso" style={{ fontSize: '14px', marginBottom: '20px', flexGrow: 1 }}>
            {product.desc}
          </p>

          {/* Price + CTA */}
          {isMini ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <span
                data-testid="product-price-miso-mini"
                style={{ fontFamily: 'Canela, Georgia, serif', fontWeight: 700, fontSize: '28px', color: '#B85C3A', letterSpacing: '-0.02em' }}
              >
                {product.price}
              </span>
              <button
                onClick={onBook}
                data-testid="product-cta-miso-mini"
                className="btn-primary"
                style={{ fontSize: '11px', padding: '12px 22px' }}
              >
                {product.cta}
              </button>
            </div>
          ) : (
            <button
              onClick={() => onNotify(product.name)}
              data-testid={`product-notify-btn-${product.id}`}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                padding: 0,
                fontFamily: 'Graphik, sans-serif',
                fontWeight: 700,
                fontSize: '11px',
                letterSpacing: '2px',
                textTransform: 'uppercase',
                color: '#9E8E7E',
                transition: 'color 0.2s ease',
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#B85C3A'}
              onMouseLeave={e => e.currentTarget.style.color = '#9E8E7E'}
            >
              Notify me when live →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Products() {
  const { openModal } = useBooking();
  const [notifyProduct, setNotifyProduct] = useState(null);
  const heading = useScrollReveal(0);
  const sub = useScrollReveal(0.1);

  return (
    <section
      id="miso-mini"
      data-testid="products-section"
      className="section-pad"
      style={{ backgroundColor: '#F2EBE0' }}
    >
      <div className="container-miso">
        {/* Header */}
        <div style={{ marginBottom: '56px' }}>
          <span ref={heading.ref} style={heading.style} className="section-label">
            Our Offerings
          </span>
          <h2 ref={sub.ref} style={{ ...sub.style, maxWidth: '520px' }} className="h2-miso">
            One live.{' '}
            <span className="italic-accent" style={{ color: '#B85C3A' }}>Two coming soon.</span>
          </h2>
          <p className="body-miso" style={{ marginTop: '12px', maxWidth: '480px' }}>
            Only Miso Mini is available to book today. Guest Booth and Miso Experience are launching soon.
          </p>
        </div>

        {/* Cards grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px',
          }}
        >
          {PRODUCTS.map((product, i) => (
            <ProductCard
              key={product.id}
              product={product}
              delay={i * 0.1}
              onBook={openModal}
              onNotify={setNotifyProduct}
            />
          ))}
        </div>
      </div>

      {/* Notify Modal */}
      {notifyProduct && (
        <NotifyModal product={notifyProduct} onClose={() => setNotifyProduct(null)} />
      )}
    </section>
  );
}
